import React, { createContext, useState, useContext, useEffect } from 'react';
import { useAuth } from './AuthContext';
import api from '../services/api';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
};

// Theme configurations
export const themes = {
  light: {
    name: 'Light',
    colors: {
      // Primary Colors
      primary: '#007bff',
      primaryDark: '#0056b3',
      primaryLight: '#e7f3ff',
      secondary: '#6c757d',
      success: '#28a745',
      danger: '#dc3545',
      dangerDark: '#a71d2a',
      warning: '#ffc107',
      info: '#17a2b8',
      
      // Neutral Colors
      white: '#ffffff',
      black: '#000000',
      gray50: '#f9fafb',
      gray100: '#f3f4f6',
      gray200: '#e5e7eb',
      gray300: '#d1d5db',
      gray400: '#9ca3af',
      gray500: '#6b7280',
      gray600: '#4b5563',
      gray700: '#374151',
      gray800: '#1f2937',
      gray900: '#111827',
      
      // Text Colors
      textPrimary: '#333333',
      textSecondary: '#6c757d',
      textMuted: '#adb5bd',
      textLight: '#f8f9fa',
      
      // Background Colors
      bgPrimary: '#ffffff',
      bgSecondary: '#f8f9fa',
      bgTertiary: '#e9ecef',
      bgDark: '#212529',
      
      // Border Colors
      borderColor: '#dee2e6',
      borderColorLight: '#e9ecef',
      borderColorDark: '#ced4da',
    }
  },
  dark: {
    name: 'Dark',
    colors: {
      // Primary Colors
      primary: '#4a9eff',
      primaryDark: '#2d7dd2',
      primaryLight: '#1a2332',
      secondary: '#8b95a1',
      success: '#3dd598',
      danger: '#ff6b6b',
      dangerDark: '#d63031',
      warning: '#ffd93d',
      info: '#4ecdc4',
      
      // Neutral Colors
      white: '#1a1d23',
      black: '#ffffff',
      gray50: '#1a1d23',
      gray100: '#22262e',
      gray200: '#2a2f38',
      gray300: '#363c47',
      gray400: '#4a5361',
      gray500: '#6b7280',
      gray600: '#9ca3af',
      gray700: '#d1d5db',
      gray800: '#e5e7eb',
      gray900: '#f3f4f6',
      
      // Text Colors
      textPrimary: '#e5e7eb',
      textSecondary: '#9ca3af',
      textMuted: '#6b7280',
      textLight: '#1a1d23',
      
      // Background Colors
      bgPrimary: '#1a1d23',
      bgSecondary: '#22262e',
      bgTertiary: '#2a2f38',
      bgDark: '#0f1115',
      
      // Border Colors
      borderColor: '#363c47',
      borderColorLight: '#2a2f38',
      borderColorDark: '#4a5361',
    }
  },
  blue: {
    name: 'Ocean Blue',
    colors: {
      // Primary Colors
      primary: '#0077be',
      primaryDark: '#005a8d',
      primaryLight: '#e6f4fb',
      secondary: '#5c7a8f',
      success: '#00b894',
      danger: '#d63031',
      dangerDark: '#a71d2a',
      warning: '#fdcb6e',
      info: '#00cec9',
      
      // Neutral Colors
      white: '#ffffff',
      black: '#000000',
      gray50: '#f0f7fb',
      gray100: '#e1eff7',
      gray200: '#c3dff0',
      gray300: '#a5cfe8',
      gray400: '#87bfe0',
      gray500: '#5c7a8f',
      gray600: '#4a6273',
      gray700: '#384a57',
      gray800: '#26323b',
      gray900: '#141a1f',
      
      // Text Colors
      textPrimary: '#1a2530',
      textSecondary: '#4a6273',
      textMuted: '#87bfe0',
      textLight: '#f0f7fb',
      
      // Background Colors
      bgPrimary: '#ffffff',
      bgSecondary: '#f0f7fb',
      bgTertiary: '#e1eff7',
      bgDark: '#1a2530',
      
      // Border Colors
      borderColor: '#c3dff0',
      borderColorLight: '#e1eff7',
      borderColorDark: '#a5cfe8',
    }
  },
  purple: {
    name: 'Royal Purple',
    colors: {
      // Primary Colors
      primary: '#6c5ce7',
      primaryDark: '#5443c4',
      primaryLight: '#f0edff',
      secondary: '#74b9ff',
      success: '#00b894',
      danger: '#ff7675',
      dangerDark: '#d63031',
      warning: '#fdcb6e',
      info: '#a29bfe',
      
      // Neutral Colors
      white: '#ffffff',
      black: '#000000',
      gray50: '#f5f3ff',
      gray100: '#ebe7ff',
      gray200: '#d7cfff',
      gray300: '#c3b7ff',
      gray400: '#af9fff',
      gray500: '#8b7fc7',
      gray600: '#6d639f',
      gray700: '#4f4777',
      gray800: '#312b4f',
      gray900: '#130f27',
      
      // Text Colors
      textPrimary: '#2d3436',
      textSecondary: '#636e72',
      textMuted: '#b2bec3',
      textLight: '#f5f3ff',
      
      // Background Colors
      bgPrimary: '#ffffff',
      bgSecondary: '#f5f3ff',
      bgTertiary: '#ebe7ff',
      bgDark: '#2d3436',
      
      // Border Colors
      borderColor: '#d7cfff',
      borderColorLight: '#ebe7ff',
      borderColorDark: '#c3b7ff',
    }
  },
  green: {
    name: 'Forest Green',
    colors: {
      // Primary Colors
      primary: '#00b894',
      primaryDark: '#00916f',
      primaryLight: '#e6f9f5',
      secondary: '#636e72',
      success: '#55efc4',
      danger: '#ff7675',
      dangerDark: '#d63031',
      warning: '#ffeaa7',
      info: '#74b9ff',
      
      // Neutral Colors
      white: '#ffffff',
      black: '#000000',
      gray50: '#f0fdf7',
      gray100: '#e1fbef',
      gray200: '#c3f7df',
      gray300: '#a5f3cf',
      gray400: '#87efbf',
      gray500: '#5c9f7f',
      gray600: '#4a7f66',
      gray700: '#385f4c',
      gray800: '#263f33',
      gray900: '#141f19',
      
      // Text Colors
      textPrimary: '#2d3436',
      textSecondary: '#636e72',
      textMuted: '#b2bec3',
      textLight: '#f0fdf7',
      
      // Background Colors
      bgPrimary: '#ffffff',
      bgSecondary: '#f0fdf7',
      bgTertiary: '#e1fbef',
      bgDark: '#2d3436',
      
      // Border Colors
      borderColor: '#c3f7df',
      borderColorLight: '#e1fbef',
      borderColorDark: '#a5f3cf',
    }
  },
  sunset: {
    name: 'Sunset Orange',
    colors: {
      // Primary Colors
      primary: '#ff6348',
      primaryDark: '#e84118',
      primaryLight: '#fff5f3',
      secondary: '#718093',
      success: '#4cd137',
      danger: '#e84118',
      dangerDark: '#c23616',
      warning: '#ffa502',
      info: '#00a8ff',
      
      // Neutral Colors
      white: '#ffffff',
      black: '#000000',
      gray50: '#fff9f7',
      gray100: '#fff3ef',
      gray200: '#ffe7df',
      gray300: '#ffdbcf',
      gray400: '#ffcfbf',
      gray500: '#b38f7f',
      gray600: '#8f7266',
      gray700: '#6b564c',
      gray800: '#473933',
      gray900: '#231d19',
      
      // Text Colors
      textPrimary: '#2f3640',
      textSecondary: '#718093',
      textMuted: '#a4b0be',
      textLight: '#fff9f7',
      
      // Background Colors
      bgPrimary: '#ffffff',
      bgSecondary: '#fff9f7',
      bgTertiary: '#fff3ef',
      bgDark: '#2f3640',
      
      // Border Colors
      borderColor: '#ffe7df',
      borderColorLight: '#fff3ef',
      borderColorDark: '#ffdbcf',
    }
  }
};

export const ThemeProvider = ({ children }) => {
  const { user } = useAuth();
  const [currentTheme, setCurrentTheme] = useState('light');
  const [loading, setLoading] = useState(true);

  // Load theme from localStorage or user preferences
  useEffect(() => {
    const loadTheme = async () => {
      try {
        // First check localStorage
        const savedTheme = localStorage.getItem('theme');
        
        // If user is logged in, try to get their preference from backend
        if (user) {
          try {
            const response = await api.get('/users/me/preferences');
            if (response.data.theme && themes[response.data.theme]) {
              setCurrentTheme(response.data.theme);
              localStorage.setItem('theme', response.data.theme);
              applyTheme(response.data.theme);
              setLoading(false);
              return;
            }
          } catch (error) {
            console.log('Could not load user theme preference:', error.message);
          }
        }
        
        // Fall back to localStorage or default
        const themeToUse = savedTheme && themes[savedTheme] ? savedTheme : 'light';
        setCurrentTheme(themeToUse);
        applyTheme(themeToUse);
      } catch (error) {
        console.error('Error loading theme:', error);
        setCurrentTheme('light');
        applyTheme('light');
      } finally {
        setLoading(false);
      }
    };

    loadTheme();
  }, [user]);

  // Apply theme to CSS variables
  const applyTheme = (themeName) => {
    const theme = themes[themeName];
    if (!theme) {
      console.error(`Theme "${themeName}" not found`);
      return;
    }

    console.log(`Applying theme: ${themeName}`, theme);
    const root = document.documentElement;
    const colors = theme.colors;

    // Apply all color variables
    Object.entries(colors).forEach(([key, value]) => {
      // Convert camelCase to kebab-case
      const cssVarName = key.replace(/([A-Z])/g, '-$1').toLowerCase();
      root.style.setProperty(`--color-${cssVarName}`, value);
      
      // Also set without color prefix for backward compatibility
      if (key.startsWith('text') || key.startsWith('bg') || key.startsWith('border')) {
        const simpleName = key.replace(/([A-Z])/g, '-$1').toLowerCase();
        root.style.setProperty(`--${simpleName}`, value);
      }
    });

    // Set data attribute for theme-specific styling
    root.setAttribute('data-theme', themeName);
    console.log(`Theme ${themeName} applied successfully. Check CSS variables in DevTools.`);
  };

  // Change theme
  const changeTheme = async (themeName) => {
    if (!themes[themeName]) {
      console.error(`Theme "${themeName}" not found`);
      return;
    }

    setCurrentTheme(themeName);
    applyTheme(themeName);
    localStorage.setItem('theme', themeName);

    // Save to backend if user is logged in
    if (user) {
      try {
        await api.put('/users/me/preferences', { theme: themeName });
      } catch (error) {
        console.error('Error saving theme preference:', error);
      }
    }
  };

  const value = {
    currentTheme,
    themes,
    changeTheme,
    loading,
    theme: themes[currentTheme],
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeContext;

// ---
